import React, { useState, useMemo } from 'react'
import { Box, IconButton, Tooltip } from '@mui/material'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import axios from 'axios'
import PlatinumTable from '../platinum-table-new/PlatinumTable'
import PlatinumGraphs from '../platinum-graphs/PlatinumGraphs'
import TableControls from '../platinum-table-new/TableControls'
import GraphViewControls from '../platinum-graphs/GraphViewControls'
import MetadataModal from '../platinum-table-new/MetadataModal'
import { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { TableViewSettingsData, TableVisibilitySettings } from '../../types/gotypes'
import { GraphConfig, ChartType, DataType } from '../platinum-graphs/types'
import { validators } from '../platinum-graphs/validators'
import { convertPlatinumToTable, extractRowLabels, extractColumnLabels, extractRowTypes, extractColumnTypes } from '../platinum-graphs/utils/convertPlatinumData'
import { getSelectedColumnLabel } from '../platinum-graphs/utils/selectionHelpers'
import { downloadSpecXlsx } from '../../utils/tableXlsxDownload'
import useSnackbar from '../../hooks/useSnackbar'
import { API_BASE_URL } from '../../constants/system'
import { CARD_RADIUS, HAIRLINE, useAgentCardHost } from '../../agent'

interface InlinePlatinumTableProps {
  platinumData: unknown
  title?: string
  customer?: string
  job?: string
  spec?: string // CarbonSpec JSON string for re-fetching with visibility overrides
  sessionId?: string // the agent session the table came from; named in the Excel stamp
  defaultViewMode?: 'table' | 'graph'
  defaultChartType?: string
  /** The Notebook cell this table is (#1869): shows "Open in notebook" when the page has a Notebook. */
  notebookCellId?: string
}

function buildSpecSubtitle(spec?: string): string | null {
  if (!spec) return null
  try {
    const s = JSON.parse(spec)
    const parts: string[] = []
    if (s.top) parts.push(`Top: ${s.top}`)
    if (s.side) parts.push(`Side: ${s.side}`)
    if (s.filter) parts.push(`Filter: ${s.filter}`)
    return parts.length > 0 ? parts.join('  \u00b7  ') : null
  } catch { return null }
}

function getInitialViewSettings(data: PlatinumData, defaultChartType?: string): TableViewSettingsData {
  const settings: TableViewSettingsData = {
    visibility: {
      frequency: false,
      column_percent: true,
      row_percent: false,
      significance: false,
    },
    vertical_coefficient: 1,
    horizontal_coefficient: 1,
    chart_type: defaultChartType || 'bar',
    data_type: 'freq',
    selected_rows: [],
    selected_columns: [],
    switch_axis: false,
  }

  const props = data?.properties
  if (props?.cells) {
    settings.visibility = {
      frequency: props.cells.frequencies ?? false,
      column_percent: props.cells.columnPercents ?? true,
      row_percent: props.cells.rowPercents ?? false,
      significance: props.significance?.visible ?? false,
    }
  }

  return settings
}

export default function InlinePlatinumTable({ platinumData, title, customer, job, spec, sessionId, defaultViewMode, defaultChartType, notebookCellId }: InlinePlatinumTableProps) {
  const { openNotebook } = useAgentCardHost()
  const [viewSettings, setViewSettings] = useState<TableViewSettingsData>(
    () => getInitialViewSettings(platinumData as PlatinumData, defaultChartType)
  )
  const [viewMode, setViewMode] = useState<'table' | 'graph'>(defaultViewMode || 'table')
  const [fullscreen, setFullscreen] = useState(false)
  const [showMetadata, setShowMetadata] = useState(false)
  const snackbar = useSnackbar()
  const [fetchedData, setFetchedData] = useState<PlatinumData | null>(null)

  const data = (fetchedData || platinumData) as PlatinumData
  const hasCells = Boolean(data?.cells)

  // 🔴 Every hook sits ABOVE the "no cells" early return below. A table can
  // arrive empty and fill on a later render; a hook after that return changes
  // the hook count between renders and React throws "Rendered more hooks".

  // Graph metadata (rows, columns, types, allowed chart types)
  const graphMetadata = useMemo(() => {
    if (!hasCells) {
      return { allRows: [], allColumns: [], allRowTypes: [], allColumnTypes: [], allowedChartTypes: ['line' as ChartType] }
    }
    const allRows = extractRowLabels(data)
    const allColumns = extractColumnLabels(data)
    const allRowTypes = extractRowTypes(data)
    const allColumnTypes = extractColumnTypes(data)

    const parsedTable = convertPlatinumToTable(data, 'freq')
    const allowedChartTypes: ChartType[] = []
    if (parsedTable) {
      for (const ct of Object.keys(validators) as ChartType[]) {
        if (validators[ct](parsedTable)) allowedChartTypes.push(ct)
      }
    }

    return {
      allRows,
      allColumns,
      allRowTypes,
      allColumnTypes,
      allowedChartTypes: allowedChartTypes.length > 0 ? allowedChartTypes : ['line' as ChartType],
    }
  }, [data, hasCells])

  // Convert viewSettings → GraphConfig for PlatinumGraphs
  const graphConfig = useMemo<GraphConfig>(() => {
    const selectedColumn = getSelectedColumnLabel(
      viewSettings.selected_column,
      graphMetadata.allColumns
    )
    return {
      chartType: viewSettings.chart_type as ChartType,
      dataType: viewSettings.data_type as DataType,
      selectedRows: viewSettings.selected_rows || [],
      selectedColumns: viewSettings.selected_columns || [],
      selectedColumn,
      switchAxis: viewSettings.switch_axis || false,
    }
  }, [viewSettings, graphMetadata.allColumns])

  if (!hasCells) return null

  // Handlers — all local state, no URL params
  const handleViewSettingsChange = (newSettings: Partial<TableViewSettingsData>) => {
    setViewSettings(prev => ({ ...prev, ...newSettings }))
  }

  const handleVisibilitySettingsChange = (newSettings: Partial<TableVisibilitySettings>) => {
    const merged = { ...viewSettings.visibility, ...newSettings }
    if (!merged.frequency && !merged.column_percent && !merged.row_percent && !merged.significance) {
      return
    }
    setViewSettings(prev => ({
      ...prev,
      visibility: { ...prev.visibility, ...newSettings },
    }))

    // Re-fetch from server with visibility overrides applied to the spec
    if (customer && job && spec) {
      const params = new URLSearchParams({ format: 'json' })
      params.set('frequency', String(merged.frequency))
      params.set('column_percent', String(merged.column_percent))
      params.set('row_percent', String(merged.row_percent))
      params.set('significance', String(merged.significance))

      const url = `${API_BASE_URL}/tables/${encodeURIComponent(customer)}/${encodeURIComponent(job)}/run?${params}`
      axios.post<PlatinumData>(url, JSON.parse(spec))
        .then(res => setFetchedData(res.data))
        .catch(err => console.error('Visibility refetch failed:', err))
    }
  }

  const handleViewModeToggle = () => {
    setViewMode(prev => prev === 'table' ? 'graph' : 'table')
  }

  const handleFullscreenToggle = () => {
    setFullscreen(prev => !prev)
  }

  const handleInfoClick = () => {
    setShowMetadata(true)
  }

  const handleDownloadXlsx = async () => {
    if (!customer || !job || !spec) return
    try {
      await downloadSpecXlsx({
        customer,
        job,
        spec: JSON.parse(spec),
        visibility: viewSettings.visibility,
        fileName: title || 'table',
        sessionId,
      })
    } catch (err) {
      snackbar.error(`Excel download failed: ${(err as Error).message}`)
    }
  }

  // Controls — reuse the same components as PlatinumTableWrapper
  const controls = viewMode === 'table' ? (
    <TableControls
      viewSettings={viewSettings}
      onViewSettingsChange={handleViewSettingsChange}
      onVisibilitySettingsChange={handleVisibilitySettingsChange}
      fullscreen={fullscreen}
      onFullscreenToggle={handleFullscreenToggle}
      onChartView={handleViewModeToggle}
      onInfoClick={handleInfoClick}
      onDownloadXlsx={customer && job && spec ? handleDownloadXlsx : undefined}
    />
  ) : (
    <GraphViewControls
      allRows={graphMetadata.allRows}
      allColumns={graphMetadata.allColumns}
      allRowTypes={graphMetadata.allRowTypes}
      allColumnTypes={graphMetadata.allColumnTypes}
      allowedChartTypes={graphMetadata.allowedChartTypes}
      viewSettings={viewSettings}
      onViewSettingsChange={handleViewSettingsChange}
      fullscreen={fullscreen}
      onFullscreenToggle={handleFullscreenToggle}
      onTableView={handleViewModeToggle}
      onInfoClick={handleInfoClick}
    />
  )

  const metadataModal = (
    <MetadataModal
      open={showMetadata}
      onClose={() => setShowMetadata(false)}
      data={data}
      tableName={title}
    />
  )

  // Fullscreen: fixed overlay filling the screen
  if (fullscreen) {
    return (
      <>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 1300,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ flexShrink: 0 }}>{controls}</Box>
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            {viewMode === 'table' ? (
              <PlatinumTable data={data} viewSettings={viewSettings} />
            ) : (
              <PlatinumGraphs
                data={data}
                config={graphConfig}
                showControls={false}
                respectParentHeight={true}
                allRowTypes={graphMetadata.allRowTypes}
                allColumnTypes={graphMetadata.allColumnTypes}
              />
            )}
          </Box>
        </Box>
        {metadataModal}
      </>
    )
  }

  // Inline: bordered card in the chat
  const specSubtitle = buildSpecSubtitle(spec)
  const isGraphMode = viewMode === 'graph'
  const showNotebook = !!openNotebook && !!notebookCellId

  return (
    <Box
      data-testid="agent-rendered-table"
      sx={{
        mt: 2,
        mb: 1,
        // The same edge and corner as every other card in the conversation
        // column; a chart no longer gets a heavier frame and a shadow of its own.
        border: HAIRLINE,
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        maxWidth: '100%',
        backgroundColor: isGraphMode ? 'white' : undefined,
      }}
    >
      {/* Title header */}
      {(title || specSubtitle || showNotebook) && (
        <Box sx={{ px: 1.5, py: 0.75, backgroundColor: '#f9fafb', borderBottom: HAIRLINE, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Box sx={{ fontSize: 13, fontWeight: 600, color: '#1f2937' }}>{title}</Box>
            )}
            {specSubtitle && (
              <Box sx={{ fontSize: 11, color: '#6b7280', mt: title ? 0.25 : 0, fontFamily: 'monospace' }}>{specSubtitle}</Box>
            )}
          </Box>
          {showNotebook && (
            <Tooltip title="Open in notebook">
              <IconButton
                size="small"
                aria-label="Open in notebook"
                data-testid="open-in-notebook"
                onClick={() => notebookCellId && openNotebook?.(notebookCellId)}
                sx={{ color: 'info.main', p: 0.25, flexShrink: 0 }}
              >
                <NotesOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      {controls}
      {viewMode === 'table' ? (
        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          <PlatinumTable data={data} viewSettings={viewSettings} />
        </Box>
      ) : (
        <Box sx={{ height: 350, backgroundColor: 'white' }}>
          <PlatinumGraphs
            data={data}
            config={graphConfig}
            showControls={false}
            respectParentHeight={true}
            allRowTypes={graphMetadata.allRowTypes}
            allColumnTypes={graphMetadata.allColumnTypes}
          />
        </Box>
      )}
      {metadataModal}
    </Box>
  )
}
